import { useTranslation } from 'react-i18next';
import { Clock, Users, DollarSign, X, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Comparison = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = t('comparison.features', { returnObjects: true }) as any[];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const itemVariantsRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl lg:text-6xl font-black text-center text-gray-900 mb-20 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {t('comparison.title')}
        </motion.h2>
        <motion.p 
          className="text-xl text-center text-gray-600 mb-12 font-semibold"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
        >
          {t('comparison.subtitle')}
        </motion.p>

        <motion.div 
          ref={ref}
          className="grid md:grid-cols-2 gap-12 mb-20"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* 人間営業マン */}
          <motion.div 
            className="bg-gray-50 rounded-3xl p-12 border-2 border-gray-200"
            variants={itemVariants}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="text-center mb-8">
              <div className="inline-block bg-gray-200 rounded-full p-6 mb-4">
                <Users size={48} className="text-gray-600" />
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-4">
                {t('comparison.human.title')}
              </h3>
            </div>
            <ul className="space-y-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-4">
                  <X size={24} className="text-red-500 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <span className="text-lg font-bold text-gray-600 block mb-1">
                      {feature.label}
                    </span>
                    <span className="text-xl text-gray-700 font-semibold">
                      {feature.human}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-12 pt-8 border-t-2 border-gray-300">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-600 mb-2">
                  {t('comparison.human.cost')}
                </p>
                <p className="text-4xl font-black text-gray-900">
                  {t('comparison.human.costValue')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* AI営業マン */}
          <motion.div 
            className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-12 border-4 border-blue-500 relative overflow-hidden"
            variants={itemVariantsRight}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="absolute top-4 right-4 bg-blue-500 text-white px-6 py-2 rounded-full text-sm font-black">
              WINNER
            </div>
            <div className="text-center mb-8">
              <div className="inline-block bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full p-6 mb-4">
                <Users size={48} className="text-white" />
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-4">
                {t('comparison.ai.title')}
              </h3>
            </div>
            <ul className="space-y-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-4">
                  <Check size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <span className="text-lg font-bold text-blue-600 block mb-1">
                      {feature.label}
                    </span>
                    <span className="text-xl text-gray-700 font-semibold">
                      {feature.ai}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-12 pt-8 border-t-2 border-blue-300">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600 mb-2">
                  {t('comparison.ai.cost')}
                </p>
                <p className="text-4xl font-black text-blue-900">
                  {t('comparison.ai.costValue')}
                </p>
                <p className="text-lg text-gray-600 mt-2 font-semibold">
                  {t('comparison.ai.roi')}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* 主要な差別化ポイント */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        >
          <motion.div 
            className="text-center p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <Clock size={64} className="text-blue-600 mx-auto mb-4" />
            <h4 className="text-2xl font-black text-gray-900 mb-2">
              {t('comparison.highlights.hours24.title')}
            </h4>
            <p className="text-gray-700 font-semibold">
              {t('comparison.highlights.hours24.description')}
            </p>
          </motion.div>
          <motion.div 
            className="text-center p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <Users size={64} className="text-blue-600 mx-auto mb-4" />
            <h4 className="text-2xl font-black text-gray-900 mb-2">
              {t('comparison.highlights.concurrent.title')}
            </h4>
            <p className="text-gray-700 font-semibold">
              {t('comparison.highlights.concurrent.description')}
            </p>
          </motion.div>
          <motion.div 
            className="text-center p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl"
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <DollarSign size={64} className="text-blue-600 mx-auto mb-4" />
            <h4 className="text-2xl font-black text-gray-900 mb-2">
              {t('comparison.highlights.cost.title')}
            </h4>
            <p className="text-gray-700 font-semibold">
              {t('comparison.highlights.cost.description')}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Comparison;

